import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../app/firebase.config";
import CanvasIcon from "./CanvasIcon";
import { selectCurrentUser } from "../features/user/userSlice";
import {
  fetchCanvasCourses,
  fetchCanvasIntegrationStatus,
  syncCanvasCourses,
} from "../utils/lessonPlanApi";
import { saveLessonPlannerContext } from "../utils/lessonPlannerContext";
import {
  getMissingVoicesSurfaceDestination,
  getTeachLeagueSurfaceDestination,
  isMissingVoicesSurfacePath as isMissingVoicesRoutePath,
} from "../utils/missingVoicesRouting";

const HEADER_SURFACES = [
  {
    key: "lesson-planner",
    label: "Lesson Planner",
    path: "/",
  },
  {
    key: "missing-voices",
    label: "Missing Voices",
    path: "/missingvoices",
  },
  {
    key: "videos",
    label: "Videos",
    path: "/videos",
  },
  {
    key: "gradebook",
    label: "Gradebook",
    path: "/mydata",
  },
];

const HEADER_CANVAS_CONTEXT_SOURCE = "header-canvas-course";

const buildCanvasCourseDocId = (userId, canvasCourseId) =>
  `canvas-${userId}-${canvasCourseId}`;

const normalizeList = (value) =>
  Array.isArray(value)
    ? value
        .map((item) =>
          typeof item === "string"
            ? item.trim()
            : typeof item?.text === "string"
              ? item.text.trim()
              : ""
        )
        .filter(Boolean)
    : [];

const buildHeaderPlannerContext = (cls, course) => {
  if (!cls) return null;

  const classLabel =
    cls.name || cls.canvasCourseName || course?.label || "this class";
  const gradeLevels = normalizeList(cls.gradeLevels);
  const standards = normalizeList(cls.standards);
  const essentialQuestions = normalizeList(cls.essentialQuestions);
  const enduringUnderstandings = normalizeList(cls.enduringUnderstandings);
  const assignmentSummary =
    typeof cls?.assignmentNameAnalysis?.summary === "string"
      ? cls.assignmentNameAnalysis.summary.trim()
      : "";
  const assignmentSections = Array.isArray(cls?.assignmentNameAnalysis?.sections)
    ? cls.assignmentNameAnalysis.sections
        .slice(0, 3)
        .map((section) => {
          if (!section?.summary || !section?.sectionName) return null;
          const sectionLabel = section.subsection
            ? `${section.sectionName} (${section.subsection})`
            : section.sectionName;
          return `${sectionLabel}: ${section.summary}`;
        })
        .filter(Boolean)
    : [];
  const knowledgeLoop = cls?.knowledgeLoop || null;

  const additionalCriteria = [
    "Imported from the header Canvas courses menu.",
    course?.meta ? `Course context: ${course.meta}.` : null,
    gradeLevels.length > 0 ? `Grade levels: ${gradeLevels.join(", ")}.` : null,
    standards.length > 0 ? `Standards focus: ${standards.join(", ")}.` : null,
    essentialQuestions.length > 0
      ? `Essential questions: ${essentialQuestions.join(" | ")}.`
      : null,
    enduringUnderstandings.length > 0
      ? `Enduring understandings: ${enduringUnderstandings.join(" | ")}.`
      : null,
    assignmentSummary ? `Recent assignment trend: ${assignmentSummary}` : null,
    assignmentSections.length > 0
      ? `Section-level assignment signals: ${assignmentSections.join(" ")}`
      : null,
    knowledgeLoop?.topic ? `Latest planned topic: ${knowledgeLoop.topic}.` : null,
    knowledgeLoop?.objectiveSummary
      ? `Latest planning objective: ${knowledgeLoop.objectiveSummary}.`
      : null,
    knowledgeLoop?.keyPointsSummary
      ? `Latest planning key points: ${knowledgeLoop.keyPointsSummary}.`
      : null,
    knowledgeLoop?.assessmentSummary
      ? `Latest planning assessment signal: ${knowledgeLoop.assessmentSummary}.`
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  return {
    source: HEADER_CANVAS_CONTEXT_SOURCE,
    createdAt: new Date().toISOString(),
    classId: String(cls.id),
    className: cls.name || cls.canvasCourseName || null,
    canvasCourseId: cls.canvasCourseId
      ? String(cls.canvasCourseId)
      : String(course?.id || "").trim() || null,
    canvasCourseName: cls.canvasCourseName || course?.label || null,
    term: cls.term || course?.meta || null,
    linkedCourseId: cls.linkedCourseId
      ? String(cls.linkedCourseId)
      : cls.courseId
        ? String(cls.courseId)
        : null,
    linkedCourseName: cls.courseName || null,
    gradeLevels,
    standards,
    schoolDistrict: cls.district || null,
    schoolUrl: cls.schoolUrl || null,
    classPeriodLength: cls.classPeriodLength || null,
    essentialQuestions,
    enduringUnderstandings,
    assignmentNameAnalysis: cls.assignmentNameAnalysis || null,
    knowledgeLoop,
    topicSuggestion: `Plan the next high-impact lesson for ${classLabel}`,
    additionalCriteria: additionalCriteria || null,
  };
};

export const isGradebookSurfacePath = (pathname = "") =>
  pathname === "/mydata" ||
  pathname.startsWith("/mydata/") ||
  pathname === "/dashboard" ||
  pathname.startsWith("/dashboard/");

export const isVideosSurfacePath = (pathname = "") =>
  pathname === "/videos" || pathname.startsWith("/videos/");

export const isMissingVoicesSurfacePath = (pathname = "") =>
  isMissingVoicesRoutePath(pathname, window.location.hostname);

const HeaderSurfaceSwitcher = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentUser = useSelector(selectCurrentUser);
  const switcherRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [canvasConnected, setCanvasConnected] = useState(false);
  const [canvasStatusResolved, setCanvasStatusResolved] = useState(false);
  const [canvasCourses, setCanvasCourses] = useState([]);
  const [canvasCoursesLoading, setCanvasCoursesLoading] = useState(false);
  const [canvasCoursesError, setCanvasCoursesError] = useState("");
  const [canvasCoursesNotice, setCanvasCoursesNotice] = useState("");
  const [plannerCourseRemovalId, setPlannerCourseRemovalId] = useState("");
  const [plannerCourseSyncId, setPlannerCourseSyncId] = useState("");
  const canvasCoursesRequestInFlightRef = useRef(false);
  const currentSurfaceKey = isGradebookSurfacePath(pathname)
    ? "gradebook"
    : isVideosSurfacePath(pathname)
      ? "videos"
    : isMissingVoicesSurfacePath(pathname)
      ? "missing-voices"
      : "lesson-planner";
  const currentSurfaceLabel =
    HEADER_SURFACES.find((surface) => surface.key === currentSurfaceKey)?.label ||
    "Lesson Planner";
  const accountName =
    currentUser?.displayName?.trim() || currentUser?.email?.trim() || "Guest";
  const accountPlanLabel = "Free";
  const accountAvatarLabel = accountName.charAt(0).toUpperCase();
  const accountPhotoUrl =
    typeof currentUser?.photoURL === "string" && currentUser.photoURL.trim()
      ? currentUser.photoURL.trim()
      : null;

  const classesByCanvasCourseId = useMemo(() => {
    const map = new Map();
    classes.forEach((cls) => {
      if (cls?.canvasListRemoved) return;
      const key = String(cls?.canvasCourseId || "").trim();
      if (!key || map.has(key)) return;
      map.set(key, cls);
    });
    return map;
  }, [classes]);

  const headerCanvasCourses = useMemo(() => {
    const mergedCourses = [];
    const seenKeys = new Set();
    const pushCourse = ({
      id,
      label,
      meta = "",
      localClass = null,
      source = "gradebook",
    }) => {
      const normalizedId = String(id || "").trim();
      const normalizedLabel = String(label || "").trim();
      if (!normalizedId || !normalizedLabel) return;
      const seenKey = `${source}:${normalizedId}`;
      if (seenKeys.has(seenKey)) return;
      seenKeys.add(seenKey);
      mergedCourses.push({
        id: normalizedId,
        label: normalizedLabel,
        meta: String(meta || "").trim(),
        localClass,
        source,
      });
    };

    canvasCourses.forEach((course) => {
      const courseId = String(course?.id || "").trim();
      if (!courseId) return;
      pushCourse({
        id: courseId,
        label:
          course?.name ||
          course?.courseCode ||
          course?.termName ||
          `Course ${courseId}`,
        meta: course?.termName || course?.courseCode || "",
        localClass: classesByCanvasCourseId.get(courseId) || null,
        source: "canvas",
      });
    });

    if (mergedCourses.length === 0) {
      classes.forEach((cls) => {
        if (cls?.canvasListRemoved) return;
        const courseId = String(cls?.canvasCourseId || cls?.id || "").trim();
        if (!courseId) return;
        pushCourse({
          id: courseId,
          label: cls?.name || cls?.canvasCourseName || "Untitled class",
          meta: cls?.term || cls?.canvasCourseName || "",
          localClass: cls,
          source: cls?.canvasCourseId ? "synced-canvas" : "gradebook",
        });
      });
    }

    return mergedCourses.sort((left, right) =>
      left.label.localeCompare(right.label, undefined, { sensitivity: "base" })
    );
  }, [canvasCourses, classes, classesByCanvasCourseId]);

  const openLoginModal = () => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("open-login-modal"));
  };

  const loadHeaderClasses = useCallback(async () => {
    if (!currentUser?.uid) {
      setClasses([]);
      return [];
    }

    try {
      const classesRef = collection(db, "teachingCourses");
      const classesQuery = query(
        classesRef,
        where("teacherId", "==", currentUser.uid),
        where("archived", "==", false),
        limit(200)
      );
      const snapshot = await getDocs(classesQuery);
      const list = snapshot.docs
        .map((courseDoc) => ({
          id: courseDoc.id,
          ...courseDoc.data(),
        }))
        .filter((cls) => !cls.canvasListRemoved);
      setClasses(list);
      return list;
    } catch (error) {
      console.error("Error loading header Canvas classes:", error);
      setCanvasCoursesError(
        error?.message || "Unable to load Canvas-linked course details."
      );
      return [];
    }
  }, [currentUser?.uid]);

  const loadHeaderCanvasCourses = useCallback(async () => {
    if (!currentUser?.uid || canvasCoursesRequestInFlightRef.current) return;

    canvasCoursesRequestInFlightRef.current = true;
    setCanvasCoursesLoading(true);
    setCanvasCoursesError("");
    try {
      const status = await fetchCanvasIntegrationStatus();
      const connected = Boolean(status?.connected);
      setCanvasConnected(connected);
      setCanvasStatusResolved(true);

      if (!connected) {
        setCanvasCourses([]);
        return;
      }

      const result = await fetchCanvasCourses();
      setCanvasCourses(Array.isArray(result?.courses) ? result.courses : []);
    } catch (error) {
      console.error("Failed to load Canvas courses from header menu:", error);
      setCanvasConnected(false);
      setCanvasStatusResolved(true);
      setCanvasCourses([]);
      setCanvasCoursesError(error?.message || "Unable to load Canvas courses.");
    } finally {
      canvasCoursesRequestInFlightRef.current = false;
      setCanvasCoursesLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!currentUser) {
      setClasses([]);
      setCanvasConnected(false);
      setCanvasStatusResolved(false);
      setCanvasCourses([]);
      setCanvasCoursesError("");
      setCanvasCoursesNotice("");
      setPlannerCourseRemovalId("");
      setPlannerCourseSyncId("");
      canvasCoursesRequestInFlightRef.current = false;
    }
  }, [currentUser]);

  useEffect(() => {
    if (!isMenuOpen || !currentUser?.uid) return;
    void loadHeaderClasses();
    void loadHeaderCanvasCourses();
  }, [isMenuOpen, currentUser?.uid, loadHeaderClasses, loadHeaderCanvasCourses]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (switcherRef.current?.contains(event.target)) return;
      setIsMenuOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  const resolveSurfaceDestination = useCallback((surfaceKey) => {
    const hostname = window.location.hostname;

    if (surfaceKey === "missing-voices") {
      return getMissingVoicesSurfaceDestination(hostname);
    }

    if (surfaceKey === "gradebook") {
      return getTeachLeagueSurfaceDestination("/mydata", hostname);
    }

    if (surfaceKey === "videos") {
      return getTeachLeagueSurfaceDestination("/videos", hostname);
    }

    return getTeachLeagueSurfaceDestination("/", hostname);
  }, []);

  const handleSurfaceSelect = (surface) => {
    setIsMenuOpen(false);

    if (!surface || surface.key === currentSurfaceKey) return;

    const destination = resolveSurfaceDestination(surface.key);

    if (destination.type === "external") {
      window.location.assign(destination.href);
      return;
    }

    navigate(destination.href);
  };

  const handleAccountSelect = () => {
    setIsMenuOpen(false);
    if (!currentUser) {
      openLoginModal();
      return;
    }
    navigate("/profile");
  };

  const resolveHeaderCourseClassFromList = useCallback((course, classList = []) => {
    const courseId = String(course?.id || "").trim();
    if (course?.localClass) {
      return course.localClass;
    }
    if (!courseId) return null;
    return (
      classList.find(
        (cls) => String(cls?.canvasCourseId || "").trim() === courseId
      ) || null
    );
  }, []);

  const resolveHeaderCourseClass = useCallback(
    (course) => resolveHeaderCourseClassFromList(course, classes),
    [classes, resolveHeaderCourseClassFromList]
  );

  const syncHeaderCourseClass = useCallback(
    async (course) => {
      const canvasCourseId = String(course?.id || "").trim();
      const courseLabel = course?.label || "This course";
      if (!currentUser?.uid || !canvasCourseId) {
        return { matchedClass: null, errorMessage: "" };
      }

      setPlannerCourseSyncId(canvasCourseId);
      setCanvasCoursesError("");
      setCanvasCoursesNotice(`Syncing ${courseLabel} into Gradebook...`);

      try {
        await syncCanvasCourses();
        const refreshedClasses = await loadHeaderClasses();
        const matchedClass = resolveHeaderCourseClassFromList(
          course,
          refreshedClasses
        );
        setCanvasCoursesNotice("");
        return { matchedClass, errorMessage: "" };
      } catch (error) {
        console.error("Failed to sync Canvas course into header menu:", error);
        const errorMessage =
          error?.message ||
          `Unable to sync ${courseLabel} into Gradebook right now.`;
        setCanvasCoursesNotice("");
        setCanvasCoursesError(errorMessage);
        return { matchedClass: null, errorMessage };
      } finally {
        setPlannerCourseSyncId("");
      }
    },
    [currentUser?.uid, loadHeaderClasses, resolveHeaderCourseClassFromList]
  );

  const headerCourseCanRemove = useCallback(
    (course) => {
      const matchedClass = resolveHeaderCourseClass(course);
      if (matchedClass) {
        return matchedClass.source === "canvas" && Boolean(matchedClass.canvasCourseId);
      }
      return Boolean(currentUser?.uid && course?.source === "canvas" && course?.id);
    },
    [currentUser?.uid, resolveHeaderCourseClass]
  );

  const handleRemoveHeaderCourse = useCallback(
    async (course) => {
      if (!currentUser?.uid) return;

      const matchedClass = resolveHeaderCourseClass(course);
      const canvasCourseId = String(
        matchedClass?.canvasCourseId || course?.id || ""
      ).trim();
      if (!canvasCourseId) return;
      if (matchedClass && matchedClass.source !== "canvas") return;
      if (!matchedClass && course?.source !== "canvas") return;

      setPlannerCourseRemovalId(canvasCourseId);
      setCanvasCoursesError("");

      try {
        const courseRef = matchedClass?.id
          ? doc(db, "teachingCourses", matchedClass.id)
          : doc(
              db,
              "teachingCourses",
              buildCanvasCourseDocId(currentUser.uid, canvasCourseId)
            );

        const payload = {
          teacherId: currentUser.uid,
          source: "canvas",
          canvasCourseId,
          name:
            matchedClass?.name ||
            course?.label ||
            course?.name ||
            `Course ${canvasCourseId}`,
          canvasCourseName:
            matchedClass?.canvasCourseName ||
            course?.label ||
            course?.name ||
            null,
          term: matchedClass?.term || course?.meta || null,
          courseCode: matchedClass?.courseCode || course?.courseCode || null,
          canvasListRemoved: true,
          canvasListRemovedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        if (!matchedClass) {
          payload.createdAt = serverTimestamp();
        }

        await setDoc(courseRef, payload, { merge: true });

        setCanvasCourses((prev) =>
          prev.filter((item) => String(item?.id || "").trim() !== canvasCourseId)
        );
        if (matchedClass?.id) {
          setClasses((prev) =>
            prev.filter((cls) => String(cls.id) !== String(matchedClass.id))
          );
        }
      } catch (error) {
        console.error("Failed to remove Canvas course from header menu:", error);
        setCanvasCoursesError(error?.message || "Unable to remove Canvas course.");
      } finally {
        setPlannerCourseRemovalId("");
      }
    },
    [currentUser?.uid, resolveHeaderCourseClass]
  );

  const handleHeaderCoursePlanStart = useCallback(
    async (course) => {
      if (!currentUser) {
        openLoginModal();
        return;
      }

      let matchedClass = resolveHeaderCourseClass(course);
      if (!matchedClass) {
        const syncResult = await syncHeaderCourseClass(course);
        matchedClass = syncResult.matchedClass;
        if (!matchedClass) {
          if (!syncResult.errorMessage) {
            setCanvasCoursesError(
              `${
                course?.label || "This course"
              } is available in Canvas, but its class details are still syncing. Please try again in a moment.`
            );
          }
          return;
        }
      }

      const confirmed =
        typeof window === "undefined"
          ? true
          : window.confirm(
              `Use ${
                course?.label || matchedClass?.name || "this course"
              } as lesson-planning context? You'll still write what you want students to know.`
            );
      if (!confirmed) return;

      const payload = buildHeaderPlannerContext(matchedClass, course);
      if (!payload || !saveLessonPlannerContext(payload)) {
        setCanvasCoursesError(
          "Unable to open Lesson Planner with this course context."
        );
        return;
      }

      setCanvasCoursesError("");
      setCanvasCoursesNotice("");
      setIsMenuOpen(false);
      navigate("/");
    },
    [currentUser, navigate, resolveHeaderCourseClass, syncHeaderCourseClass]
  );

  return (
    <div
      className="header-surface-switcher header-surface-switcher--mobile"
      ref={switcherRef}
    >
      <button
        type="button"
        className={`header-surface-switcher__mobile-toggle${isMenuOpen ? " is-open" : ""}`}
        aria-label={`Open page navigation. Current page: ${currentSurfaceLabel}`}
        aria-expanded={isMenuOpen}
        aria-haspopup="menu"
        aria-controls="header-surface-switcher-menu"
        onClick={() => setIsMenuOpen((open) => !open)}
      >
        <span className="header-surface-switcher__mobile-bars" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </button>
      {isMenuOpen ? (
        <div
          id="header-surface-switcher-menu"
          className="header-surface-switcher__mobile-menu"
          role="menu"
          aria-label="Page navigation"
        >
          {HEADER_SURFACES.map((surface) => (
            <button
              key={surface.key}
              type="button"
              className={`header-surface-switcher__mobile-item${
                surface.key === currentSurfaceKey ? " is-active" : ""
              }`}
              onClick={() => handleSurfaceSelect(surface)}
              role="menuitemradio"
              aria-checked={surface.key === currentSurfaceKey}
              aria-label={surface.label}
            >
              <span className="header-surface-switcher__mobile-item-label">
                {surface.label}
              </span>
            </button>
          ))}

          <div className="header-surface-switcher__canvas-section" role="presentation">
            <div className="header-surface-switcher__canvas-header">
              <CanvasIcon size={15} />
              <span>Canvas Courses</span>
            </div>

            {!currentUser ? (
              <div className="header-surface-switcher__canvas-message">
                Sign in to view Canvas courses.
              </div>
            ) : null}

            {currentUser && canvasCoursesLoading ? (
              <div className="header-surface-switcher__canvas-message">
                Loading Canvas courses...
              </div>
            ) : null}

            {currentUser && canvasStatusResolved && !canvasConnected && !canvasCoursesLoading ? (
              <div className="header-surface-switcher__canvas-message">
                Connect Canvas from Gradebook to load courses.
              </div>
            ) : null}

            {currentUser && canvasConnected && headerCanvasCourses.length > 0 ? (
              <div className="header-surface-switcher__canvas-list">
                {headerCanvasCourses.map((course) => {
                  const isRemoving =
                    plannerCourseRemovalId === String(course?.id || "").trim();
                  const isSyncing =
                    plannerCourseSyncId === String(course?.id || "").trim();
                  const canRemove = headerCourseCanRemove(course);

                  return (
                    <div
                      key={`${course.source}-${course.id}`}
                      className="header-surface-switcher__canvas-course-row"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        className="header-surface-switcher__canvas-course-button"
                        onClick={() => void handleHeaderCoursePlanStart(course)}
                        disabled={isSyncing}
                      >
                        <span className="header-surface-switcher__canvas-course-name">
                          {course.label}
                        </span>
                        {course.meta ? (
                          <span className="header-surface-switcher__canvas-course-meta">
                            {course.meta}
                          </span>
                        ) : null}
                      </button>
                      {canRemove ? (
                        <button
                          type="button"
                          className="header-surface-switcher__canvas-course-remove"
                          onClick={() => handleRemoveHeaderCourse(course)}
                          disabled={isRemoving || isSyncing}
                          aria-label={`Remove ${course.label} from Canvas lists`}
                        >
                          {isRemoving ? "..." : "×"}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}

            {currentUser &&
            canvasConnected &&
            !canvasCoursesLoading &&
            headerCanvasCourses.length === 0 ? (
              <div className="header-surface-switcher__canvas-message">
                No Canvas courses found yet.
              </div>
            ) : null}

            {canvasCoursesNotice ? (
              <div className="header-surface-switcher__canvas-message">
                {canvasCoursesNotice}
              </div>
            ) : null}

            {canvasCoursesError ? (
              <div className="header-surface-switcher__canvas-message header-surface-switcher__canvas-message--error">
                {canvasCoursesError}
              </div>
            ) : null}
          </div>

          <hr className="header-surface-switcher__mobile-divider" />
          <button
            type="button"
            className="header-surface-switcher__account"
            onClick={handleAccountSelect}
            role="menuitem"
            aria-label={
              currentUser ? "Open profile settings" : "Sign in to open profile settings"
            }
          >
            {accountPhotoUrl ? (
              <img
                src={accountPhotoUrl}
                alt=""
                className="header-surface-switcher__account-avatar"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span
                className="header-surface-switcher__account-avatar header-surface-switcher__account-avatar--fallback"
                aria-hidden="true"
              >
                {accountAvatarLabel}
              </span>
            )}
            <div className="header-surface-switcher__account-copy">
              <span className="header-surface-switcher__account-name">
                {accountName}
              </span>
              <span className="header-surface-switcher__account-plan">
                {accountPlanLabel}
              </span>
            </div>
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default HeaderSurfaceSwitcher;
